import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../../components/Layout';
import API_BASE_URL from '../../config/api';
import { useAlert } from '../../context/AlertContext';
import { Save, FileText, Receipt, Hash, Percent, Type, Eye, Upload, X, Image } from 'lucide-react';

const BillSettings = () => {
    const { showAlert } = useAlert();
    const [settings, setSettings] = useState({
        bill_company_name: '',
        bill_gst_number: '',
        bill_header_text: '',
        bill_gst_note: '',
        bill_footer_text: '',
        bill_cgst_rate: '0',
        bill_sgst_rate: '0',
        bill_igst_rate: '0',
        bill_receipt_prefix: 'REC',
        bill_starting_number: '1000001',
        bill_logo_url: ''
    });
    const [saving, setSaving] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const fetchSettings = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/admin/bill_settings.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success && data.data) {
                setSettings(prev => ({ ...prev, ...data.data }));
            }
        } catch (error) {
            console.error('Error fetching bill settings:', error);
        }
    }, []);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({ ...prev, [name]: value }));
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate on client
        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/svg+xml'];
        if (!allowedTypes.includes(file.type)) {
            showAlert('Invalid file type. Allowed: PNG, JPG, GIF, WebP, SVG', 'error');
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            showAlert('File too large. Maximum size: 2MB', 'error');
            return;
        }

        setUploading(true);
        try {
            const token = localStorage.getItem('authToken');
            const formData = new FormData();
            formData.append('logo', file);

            const response = await fetch(`${API_BASE_URL}/admin/upload_logo.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            const data = await response.json();
            if (data.success) {
                setSettings(prev => ({ ...prev, bill_logo_url: data.logo_url }));
                showAlert('Logo uploaded successfully!', 'success');
            } else {
                showAlert(data.message || 'Upload failed', 'error');
            }
        } catch (error) {
            showAlert('Failed to upload logo', 'error');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const removeLogo = () => {
        setSettings(prev => ({ ...prev, bill_logo_url: '' }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/admin/bill_settings.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(settings)
            });
            const data = await response.json();
            if (data.success) {
                showAlert('Bill settings saved successfully!', 'success');
            } else {
                showAlert(data.message || 'Failed to save', 'error');
            }
        } catch (error) {
            showAlert('Failed to save bill settings', 'error');
        } finally {
            setSaving(false);
        }
    };

    // Preview data — GST INCLUSIVE: total stays 9600, base is reverse-calculated
    const totalTaxRate = parseFloat(settings.bill_cgst_rate || 0) + parseFloat(settings.bill_sgst_rate || 0) + parseFloat(settings.bill_igst_rate || 0);
    const previewTotal = 9600;
    const previewBase = totalTaxRate > 0 ? (previewTotal / (1 + totalTaxRate / 100)) : previewTotal;
    const previewCgst = (previewBase * parseFloat(settings.bill_cgst_rate || 0) / 100);
    const previewSgst = (previewBase * parseFloat(settings.bill_sgst_rate || 0) / 100);
    const previewIgst = (previewBase * parseFloat(settings.bill_igst_rate || 0) / 100);
    const previewSubtotal = previewTotal - previewCgst - previewSgst - previewIgst;

    const previewInvoice = {
        invoice_number: `${settings.bill_receipt_prefix}${settings.bill_starting_number}`,
        customer_name: 'SAMPLE CUSTOMER',
        customer_id: 'NX150007',
        customer_address: 'Sample Address, City, State - 560001',
        invoice_date: new Date().toISOString().split('T')[0],
        invoice_type: 'epin',
        items: [{ sr_no: 1, description: 'Sample Package E-Pin', quantity: 1, rate: previewSubtotal.toFixed(2), amount: previewSubtotal.toFixed(2) }],
        subtotal: previewSubtotal.toFixed(2),
        cgst_rate: settings.bill_cgst_rate,
        cgst_amount: previewCgst.toFixed(2),
        sgst_rate: settings.bill_sgst_rate,
        sgst_amount: previewSgst.toFixed(2),
        igst_rate: settings.bill_igst_rate,
        igst_amount: previewIgst.toFixed(2),
        total_amount: previewTotal.toFixed(2),
        amount_in_words: 'Rs. Nine Thousand Six Hundred Only'
    };

    const BLUE = { primary: '#1a3c6e', light: '#2c5aa0', bg: '#eaf0f7', text: '#1a3c6e', muted: '#5a6f85', border: '#c8d6e5', lightBg: '#f4f7fb' };
    const hasTax = parseFloat(previewInvoice.cgst_amount) > 0 || parseFloat(previewInvoice.sgst_amount) > 0 || parseFloat(previewInvoice.igst_amount) > 0;

    return (
        <Layout>
            <div className="space-y-6 max-w-4xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-black text-gray-900 flex items-center gap-2">
                            <Receipt className="text-blue-700" size={28} />
                            Bill / Invoice Settings
                        </h1>
                        <p className="text-gray-500 text-sm mt-1 font-medium">Configure your invoice template and billing information</p>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => setShowPreview(true)}
                            className="px-4 py-2.5 bg-blue-800 text-white font-bold rounded-xl text-xs uppercase tracking-widest hover:bg-blue-900 transition-all flex items-center gap-2 shadow-lg shadow-blue-200">
                            <Eye size={16} /> Preview Bill
                        </button>
                    </div>
                </div>

                {/* Company & Branding */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center gap-3 pb-4 border-b border-gray-100 mb-6">
                        <div className="p-2 bg-blue-50 text-blue-700 rounded-xl">
                            <FileText size={22} />
                        </div>
                        <div>
                            <h3 className="text-lg font-black text-gray-900">Company & Branding</h3>
                            <p className="text-xs text-gray-400 font-medium">Appears on all invoices</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div className="md:col-span-2">
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Company Name on Bill</label>
                            <input type="text" name="bill_company_name" value={settings.bill_company_name} onChange={handleChange}
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none font-bold text-blue-800 text-lg"
                                placeholder="Your Company Name" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">GST Number</label>
                            <input type="text" name="bill_gst_number" value={settings.bill_gst_number} onChange={handleChange}
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none font-bold"
                                placeholder="e.g. 27AAGCE8225Q1Z0" />
                        </div>

                        {/* Logo Upload */}
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Company Logo</label>
                            <div className="space-y-3">
                                {settings.bill_logo_url ? (
                                    <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl border border-blue-100">
                                        <div className="w-16 h-16 bg-white border border-blue-200 rounded-xl overflow-hidden flex items-center justify-center p-1 flex-shrink-0">
                                            <img src={settings.bill_logo_url} alt="Logo" className="max-h-full max-w-full object-contain" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-bold text-blue-800 truncate">{settings.bill_logo_url.split('/').pop()}</p>
                                            <p className="text-[10px] text-blue-500 mt-0.5">Logo will appear on all invoices</p>
                                        </div>
                                        <div className="flex gap-1 flex-shrink-0">
                                            <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                                                className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors" title="Change Logo">
                                                <Upload size={16} />
                                            </button>
                                            <button onClick={removeLogo}
                                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Remove Logo">
                                                <X size={16} />
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                                            className="w-full flex flex-col items-center gap-2 p-5 border-2 border-dashed border-gray-300 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-all cursor-pointer disabled:opacity-50">
                                            {uploading ? (
                                                <>
                                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                                    <span className="text-xs font-bold text-blue-600">Uploading...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="p-3 bg-gray-100 rounded-full">
                                                        <Image size={24} className="text-gray-400" />
                                                    </div>
                                                    <span className="text-xs font-bold text-gray-500">Click to upload logo</span>
                                                    <span className="text-[10px] text-gray-400">PNG, JPG, SVG • Max 2MB</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}

                                <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml"
                                    onChange={handleLogoUpload} className="hidden" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Header & Footer Text */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center gap-3 pb-4 border-b border-gray-100 mb-6">
                        <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">
                            <Type size={22} />
                        </div>
                        <div>
                            <h3 className="text-lg font-black text-gray-900">Bill Content</h3>
                            <p className="text-xs text-gray-400 font-medium">Header description, GST note and footer</p>
                        </div>
                    </div>

                    <div className="space-y-5">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Header Text (Below Company Name)</label>
                            <textarea name="bill_header_text" value={settings.bill_header_text} onChange={handleChange} rows={3}
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm resize-none"
                                placeholder="e.g. For pickup centre or district stockist product delivery chalan cum payment receipt" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">GST Note (Shown next to GST Number)</label>
                            <input type="text" name="bill_gst_note" value={settings.bill_gst_note} onChange={handleChange}
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                                placeholder="e.g. This is not gst bill only automated Payment receipt" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Footer Text</label>
                            <textarea name="bill_footer_text" value={settings.bill_footer_text} onChange={handleChange} rows={2}
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm resize-none"
                                placeholder="Optional: Terms and conditions, thank you note, etc." />
                        </div>
                    </div>
                </div>

                {/* GST Tax Rates */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center gap-3 pb-4 border-b border-gray-100 mb-6">
                        <div className="p-2 bg-green-50 text-green-600 rounded-xl">
                            <Percent size={22} />
                        </div>
                        <div>
                            <h3 className="text-lg font-black text-gray-900">Tax Rates</h3>
                            <p className="text-xs text-gray-400 font-medium">GST rates applied to invoices (set 0 to disable)</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {['cgst', 'sgst', 'igst'].map(tax => (
                            <div key={tax}>
                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">{tax.toUpperCase()} Rate (%)</label>
                                <input type="number" step="0.01" name={`bill_${tax}_rate`} value={settings[`bill_${tax}_rate`]} onChange={handleChange}
                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:outline-none font-bold text-lg" />
                            </div>
                        ))}
                    </div>
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-100 rounded-xl">
                        <p className="text-xs text-amber-700 font-medium">💡 Set all rates to 0 if you don't want GST on invoices. CGST + SGST apply for intra-state, IGST for inter-state.</p>
                    </div>
                </div>

                {/* Invoice Numbering */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center gap-3 pb-4 border-b border-gray-100 mb-6">
                        <div className="p-2 bg-purple-50 text-purple-600 rounded-xl">
                            <Hash size={22} />
                        </div>
                        <div>
                            <h3 className="text-lg font-black text-gray-900">Invoice Numbering</h3>
                            <p className="text-xs text-gray-400 font-medium">Configure receipt number format</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Receipt Prefix</label>
                            <input type="text" name="bill_receipt_prefix" value={settings.bill_receipt_prefix} onChange={handleChange}
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:outline-none font-bold" placeholder="REC" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Starting Number</label>
                            <input type="number" name="bill_starting_number" value={settings.bill_starting_number} onChange={handleChange}
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:outline-none font-bold" placeholder="1000001" />
                        </div>
                    </div>
                    <div className="mt-4 p-4 bg-purple-50 border border-purple-100 rounded-xl">
                        <p className="text-xs text-purple-700 font-bold">
                            Preview: <span className="text-purple-900 text-sm">{settings.bill_receipt_prefix}{settings.bill_starting_number}</span>
                        </p>
                    </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end">
                    <button onClick={handleSave} disabled={saving}
                        className="px-8 py-3 bg-blue-800 text-white font-black rounded-2xl hover:bg-blue-900 transition-all shadow-xl shadow-blue-100 flex items-center gap-2 disabled:opacity-50 text-sm uppercase tracking-widest">
                        <Save size={18} />
                        {saving ? 'Saving...' : 'Save All Bill Settings'}
                    </button>
                </div>
            </div>

            {/* Live Preview Modal — professional blue design */}
            {showPreview && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[95vh] flex flex-col overflow-hidden">
                        <div className="flex items-center justify-between px-6 py-3" style={{ background: 'linear-gradient(135deg, #1a3c6e, #2c5aa0)' }}>
                            <h3 className="font-black text-white">📄 Bill Preview (Sample Data)</h3>
                            <button onClick={() => setShowPreview(false)} className="p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors text-white">✕</button>
                        </div>
                        <div className="overflow-y-auto flex-1 p-4 bg-gray-50">
                            <div style={{
                                maxWidth: '760px', margin: '0 auto', background: '#fff',
                                fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                                border: `2px solid ${BLUE.primary}`, borderRadius: '4px', overflow: 'hidden'
                            }}>
                                {/* Header */}
                                <div style={{
                                    background: `linear-gradient(135deg, ${BLUE.primary}, ${BLUE.light})`,
                                    padding: '20px 25px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '15px'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
                                        {settings.bill_logo_url && (
                                            <div style={{ background: '#fff', borderRadius: '10px', padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '60px', minHeight: '60px' }}>
                                                <img src={settings.bill_logo_url} alt="Logo" style={{ maxHeight: '50px', maxWidth: '100px', objectFit: 'contain' }} />
                                            </div>
                                        )}
                                        <div>
                                            <div style={{ fontSize: '20px', fontWeight: '800', color: '#fff', letterSpacing: '1px' }}>
                                                {settings.bill_company_name || 'YOUR COMPANY NAME'}
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: '24px', fontWeight: '900', color: 'rgba(255,255,255,0.3)', letterSpacing: '3px' }}>INVOICE</div>
                                        <div style={{ fontSize: '14px', fontWeight: '700', color: '#fff', marginTop: '2px' }}>{previewInvoice.invoice_number}</div>
                                    </div>
                                </div>

                                {settings.bill_header_text && (
                                    <div style={{ padding: '8px 25px', background: BLUE.lightBg, borderBottom: `1px solid ${BLUE.border}`, textAlign: 'center' }}>
                                        <div style={{ fontSize: '11px', fontWeight: '600', color: BLUE.muted }}>{settings.bill_header_text}</div>
                                    </div>
                                )}

                                {settings.bill_gst_number && (
                                    <div style={{ padding: '8px 25px', borderBottom: `1px solid ${BLUE.border}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <span style={{ background: BLUE.bg, color: BLUE.text, padding: '3px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '700' }}>
                                            GST: {settings.bill_gst_number}
                                        </span>
                                        {settings.bill_gst_note && <span style={{ color: BLUE.muted, fontSize: '10px', fontStyle: 'italic' }}>({settings.bill_gst_note})</span>}
                                    </div>
                                )}

                                {/* Customer Info */}
                                <div style={{ padding: '18px 25px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', borderBottom: `2px solid ${BLUE.primary}` }}>
                                    <div>
                                        <div style={{ fontSize: '9px', fontWeight: '800', color: BLUE.light, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '8px' }}>BILL TO</div>
                                        <div style={{ fontSize: '15px', fontWeight: '700', color: '#1a2332' }}>SAMPLE CUSTOMER</div>
                                        <div style={{ fontSize: '12px', color: BLUE.muted, marginTop: '3px' }}>ID: <strong style={{ color: BLUE.primary }}>NX150007</strong></div>
                                        <div style={{ fontSize: '11px', color: BLUE.muted, marginTop: '4px' }}>Sample Address, City, State - 560001</div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: '9px', fontWeight: '800', color: BLUE.light, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '8px' }}>INVOICE DETAILS</div>
                                        <div style={{ fontSize: '12px', color: BLUE.muted }}>Date: <strong style={{ color: '#1a2332' }}>{new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</strong></div>
                                        <div style={{ fontSize: '12px', color: BLUE.muted, marginTop: '2px' }}>Receipt: <strong style={{ color: BLUE.primary }}>{previewInvoice.invoice_number}</strong></div>
                                        <div style={{ fontSize: '12px', color: BLUE.muted, marginTop: '2px' }}>Type: <strong style={{ color: '#1a2332' }}>E-Pin</strong></div>
                                    </div>
                                </div>

                                {/* Items Table */}
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr>
                                            {['#', 'Description', 'Qty', 'Rate (₹)', 'Amount (₹)'].map((h, i) => (
                                                <th key={i} style={{
                                                    padding: '12px 12px',
                                                    textAlign: i === 0 ? 'center' : i >= 3 ? 'right' : 'left',
                                                    fontSize: '10px', fontWeight: '800', color: BLUE.text,
                                                    background: BLUE.bg, borderBottom: `2px solid ${BLUE.primary}`,
                                                    textTransform: 'uppercase', letterSpacing: '1px',
                                                    width: [50, undefined, 70, 100, 110][i]
                                                }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr style={{ borderBottom: `1px solid ${BLUE.border}` }}>
                                            <td style={{ padding: '10px 12px', fontSize: '12px', textAlign: 'center', color: BLUE.muted, fontWeight: '600' }}>1</td>
                                            <td style={{ padding: '10px 12px', fontSize: '12px', color: '#1a2332', fontWeight: '500' }}>Sample Package E-Pin</td>
                                            <td style={{ padding: '10px 12px', fontSize: '12px', textAlign: 'center', color: '#1a2332' }}>1</td>
                                            <td style={{ padding: '10px 12px', fontSize: '12px', textAlign: 'right', color: '#1a2332' }}>₹{parseFloat(previewSubtotal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                            <td style={{ padding: '10px 12px', fontSize: '12px', textAlign: 'right', color: '#1a2332', fontWeight: '600' }}>₹{parseFloat(previewSubtotal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                        </tr>
                                        {hasTax && (
                                            <>
                                                <tr style={{ background: BLUE.lightBg }}>
                                                    <td colSpan="4" style={{ padding: '8px 12px', fontSize: '12px', textAlign: 'right', fontWeight: '600', color: BLUE.muted }}>Subtotal</td>
                                                    <td style={{ padding: '8px 12px', fontSize: '12px', textAlign: 'right', fontWeight: '600', color: '#1a2332' }}>₹{parseFloat(previewSubtotal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                                </tr>
                                                {parseFloat(previewInvoice.cgst_amount) > 0 && (
                                                    <tr><td colSpan="4" style={{ padding: '6px 12px', fontSize: '11px', textAlign: 'right', color: BLUE.muted }}>CGST ({settings.bill_cgst_rate}%)</td>
                                                        <td style={{ padding: '6px 12px', fontSize: '11px', textAlign: 'right', color: '#1a2332' }}>₹{previewInvoice.cgst_amount}</td></tr>
                                                )}
                                                {parseFloat(previewInvoice.sgst_amount) > 0 && (
                                                    <tr><td colSpan="4" style={{ padding: '6px 12px', fontSize: '11px', textAlign: 'right', color: BLUE.muted }}>SGST ({settings.bill_sgst_rate}%)</td>
                                                        <td style={{ padding: '6px 12px', fontSize: '11px', textAlign: 'right', color: '#1a2332' }}>₹{previewInvoice.sgst_amount}</td></tr>
                                                )}
                                                {parseFloat(previewInvoice.igst_amount) > 0 && (
                                                    <tr><td colSpan="4" style={{ padding: '6px 12px', fontSize: '11px', textAlign: 'right', color: BLUE.muted }}>IGST ({settings.bill_igst_rate}%)</td>
                                                        <td style={{ padding: '6px 12px', fontSize: '11px', textAlign: 'right', color: '#1a2332' }}>₹{previewInvoice.igst_amount}</td></tr>
                                                )}
                                            </>
                                        )}
                                        <tr>
                                            <td colSpan="4" style={{ padding: '14px 12px', fontSize: '14px', textAlign: 'right', fontWeight: '800', color: '#fff', background: BLUE.primary, letterSpacing: '0.5px' }}>TOTAL AMOUNT {hasTax && <span style={{ fontSize: '10px', fontWeight: '600', opacity: 0.8 }}>(Incl. GST)</span>}</td>
                                            <td style={{ padding: '14px 12px', fontSize: '16px', textAlign: 'right', fontWeight: '900', color: '#fff', background: BLUE.primary }}>₹{previewInvoice.total_amount}</td>
                                        </tr>
                                    </tbody>
                                </table>

                                <div style={{ padding: '12px 25px', background: BLUE.lightBg, borderTop: `1px solid ${BLUE.border}` }}>
                                    <div style={{ fontSize: '10px', fontWeight: '700', color: BLUE.light, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '3px' }}>Amount in Words</div>
                                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a2332', fontStyle: 'italic' }}>{previewInvoice.amount_in_words}</div>
                                </div>

                                <div style={{ padding: '15px 25px', borderTop: `1px solid ${BLUE.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '15px' }}>
                                    <div>
                                        {settings.bill_gst_number && (
                                            <div style={{ background: BLUE.bg, color: BLUE.text, padding: '5px 14px', borderRadius: '20px', fontSize: '10px', fontWeight: '700', display: 'inline-block' }}>
                                                GST: {settings.bill_gst_number}
                                            </div>
                                        )}
                                        {settings.bill_footer_text && <div style={{ fontSize: '10px', color: BLUE.muted, marginTop: '6px', maxWidth: '350px' }}>{settings.bill_footer_text}</div>}
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ borderTop: `1px solid ${BLUE.border}`, paddingTop: '6px', minWidth: '150px' }}>
                                            <div style={{ fontSize: '10px', fontWeight: '600', color: BLUE.muted }}>Authorized Signatory</div>
                                            <div style={{ fontSize: '11px', fontWeight: '700', color: BLUE.primary, marginTop: '2px' }}>{settings.bill_company_name || 'YOUR COMPANY'}</div>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ background: BLUE.primary, padding: '8px 25px', textAlign: 'center', fontSize: '9px', color: 'rgba(255,255,255,0.5)', letterSpacing: '1px' }}>
                                    This is a computer-generated invoice.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default BillSettings;
